import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getModelos, deleteModelo } from '../services/api';
import ConfirmDialog from '../../../common/ConfirmDialog';

/* Ícones (SVG inline — leves e sem dependências) */
const PlusIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const EditIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);
const TrashIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
);

const ModelCard = ({ modelo, onEdit, onAskDelete }) => {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <h3 className="text-lg sm:text-xl font-bold text-gray-900">{modelo.titulo}</h3>
      {modelo.descricao && (
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">
          {modelo.descricao}
        </p>
      )}

      <div className="mt-4 flex items-center gap-4">
        {/* Editar - botão roxo cheio */}
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white font-semibold shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
        >
          <EditIcon className="h-4 w-4" />
          Editar
        </button>

        {/* Excluir - minimalista (texto/ícone vermelho, sem fundo/borda) */}
        <button
          type="button"
          onClick={onAskDelete}
          className="inline-flex items-center gap-2 px-3 py-2 font-medium text-red-600 hover:text-red-700 focus:outline-none
                     !bg-transparent !border-0 !shadow-none hover:!bg-transparent active:!bg-transparent"
        >
          <TrashIcon className="h-4 w-4" />
          Excluir
        </button>
      </div>
    </div>
  );
};

const ModelosPage = () => {
  const [modelos, setModelos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  const loadModelos = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getModelos();
      setModelos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar modelos.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadModelos();
  }, []);

  const confirmDelete = (id) => setDeleteId(id);

  const handleDelete = async () => {
    try {
      await deleteModelo(deleteId);
      setDeleteId(null);
      toast.success('Modelo excluído com sucesso!');
      loadModelos();
    } catch (err) {
      toast.error(`Erro ao excluir modelo: ${err.message}. É provável que este modelo já esteja em uso e não possa ser apagado.`);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Modelos de Checklist</h1>
        <button
          type="button"
          onClick={() => navigate('/checklist/modelos/novo')}
          className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white font-bold shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
        >
          <PlusIcon className="h-5 w-5" />
          Novo Modelo
        </button>
      </div>

      {/* Estado de carregamento/erro */}
      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 rounded-2xl border border-gray-100 bg-white animate-pulse" />
          ))}
        </div>
      )}
      {!isLoading && error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Lista */}
      {!isLoading && !error && (
        modelos.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center text-gray-500">
            Nenhum modelo cadastrado.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {modelos.map((m) => (
              <ModelCard
                key={m.modelo_id}
                modelo={m}
                onEdit={() => navigate(`/checklist/modelos/${m.modelo_id}`)}
                onAskDelete={() => confirmDelete(m.modelo_id)}
              />
            ))}
          </div>
        )
      )}

      <ConfirmDialog
        open={deleteId !== null}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este modelo? Esta ação não pode ser desfeita."
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />
    </div>
  );
};

export default ModelosPage;